import { useState, useEffect, useCallback } from 'react';
import { getAuth, sendEmailVerification } from 'firebase/auth';
import { FaEnvelope, FaTimes, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';

const COOLDOWN = 60;

function EmailVerifyModal({ onVerified, onCancel, requireSend = false }) {
  const [sending, setSending]       = useState(false);
  const [checking, setChecking]     = useState(false);
  const [cooldown, setCooldown]     = useState(0);
  const [sentOnce, setSentOnce]     = useState(false);
  const [error, setError]           = useState('');
  const [successMsg, setSuccessMsg] = useState('');

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleSend = useCallback(async () => {
    setError('');
    setSuccessMsg('');
    setSending(true);
    try {
      await sendEmailVerification(getAuth().currentUser);
      setSentOnce(true);
      setCooldown(COOLDOWN);
      setSuccessMsg('Verification email sent — check your inbox.');
    } catch (e) {
      if (e.code === 'auth/too-many-requests') {
        setSentOnce(true);
        setSuccessMsg('A verification email was already sent recently — check your inbox and click the link, then continue.');
      } else {
        setError(e.message || 'Failed to send verification email.');
      }
    } finally {
      setSending(false);
    }
  }, []);

  const handleContinue = useCallback(async () => {
    setError('');
    setChecking(true);
    try {
      await getAuth().currentUser.reload();
      if (getAuth().currentUser.emailVerified) {
        onVerified();
      } else {
        setError('Email not yet verified. Please click the link in your inbox first, then try again.');
      }
    } catch (e) {
      setError(e.message || 'Could not check verification status.');
    } finally {
      setChecking(false);
    }
  }, [onVerified]);

  return (
    <div className="evmod-overlay" onClick={onCancel}>
      <div className="evmod-card" onClick={e => e.stopPropagation()}>
        <button className="evmod-close" onClick={onCancel}><FaTimes /></button>

        <div className="evmod-icon-wrap">
          <FaEnvelope className="evmod-icon" />
        </div>

        <h2 className="evmod-title">Verify Your Email</h2>
        <p className="evmod-desc">
          A verified email is required before connecting a bank account.
          {sentOnce && ' We sent a link to your inbox — click it, then come back.'}
        </p>

        {successMsg && (
          <div className="evmod-alert evmod-alert-success">
            <FaCheckCircle /> {successMsg}
          </div>
        )}
        {error && (
          <div className="evmod-alert evmod-alert-error">
            <FaExclamationCircle /> {error}
          </div>
        )}

        <div className="evmod-actions">
          <button
            className="evmod-btn evmod-btn-primary"
            onClick={handleSend}
            disabled={sending || cooldown > 0}
          >
            {sending
              ? 'Sending…'
              : cooldown > 0
                ? `Resend in ${cooldown}s`
                : sentOnce ? 'Resend Email' : 'Send Verification Email'}
          </button>

          <button
            className="evmod-btn evmod-btn-success"
            onClick={handleContinue}
            disabled={checking || (requireSend && !sentOnce)}
            title={requireSend && !sentOnce ? 'Send the verification email first' : undefined}
          >
            {checking ? 'Checking…' : "I've Verified — Continue"}
          </button>

          <button className="evmod-btn evmod-btn-cancel" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

export default EmailVerifyModal;
